import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';
import Layout from '../UI/introGameLayout/layout';

import { parseDescription } from '../../utils';

import { generalInfoQuery, scenarioLevelsQuery } from './api';
import { gameQuestionsLimit } from './data';

import './styles.css';

const parseApcDescription = (raw = '') => {
  if (!raw) return { title: '', body: '' };
  // Source convention: <h1>Title<h1> Body — the second <h1> may be missing its slash.
  const match = raw.match(/<h1>([\s\S]*?)<\/?h1>([\s\S]*)/i);
  if (match) {
    return { title: match[1].trim(), body: match[2].trim() };
  }
  // Fallback to the shared "Title: Body" convention.
  const { subtitle, description } = parseDescription(raw);
  return { title: subtitle || '', body: description || '' };
};

export default function ThePrinciplesApcIntro() {
  const navigate = useNavigate();
  const params = useParams();
  const levelNumber = Number(params.levelNumber);

  const { getCurrentThePrinciplesGameId } = useAuth();
  const gameId = getCurrentThePrinciplesGameId();

  const { data: generalInfo, isLoading: generalInfoLoading } = useQuery(generalInfoQuery());
  const game = generalInfo?.games?.find(g => g.game_type.includes('The Principles') && g.id === gameId);
  const scenarioId = game?.scenario;

  const { data: scenarioLevels, isLoading: levelsLoading } = useQuery({
    ...scenarioLevelsQuery({ scenarioId }),
    enabled: !!scenarioId,
  });

  const scenarioLevel = scenarioLevels?.find(level => level.level === levelNumber);
  const isApcLevel = scenarioLevel?.name === 'assumptions-principles-constraints';

  // Guard: if the level isn't APC, skip the intro.
  useEffect(() => {
    if (!generalInfoLoading && !levelsLoading && scenarioLevel && !isApcLevel) {
      navigate(`/the-principles/level/${levelNumber}`, { replace: true });
    }
  }, [generalInfoLoading, levelsLoading, scenarioLevel, isApcLevel, levelNumber, navigate]);

  if (generalInfoLoading || !scenarioId || levelsLoading || !scenarioLevel || !isApcLevel) {
    return null;
  }

  const { title, body } = parseApcDescription(scenarioLevel.description);
  const totalQuestions = Math.min(
    gameQuestionsLimit,
    scenarioLevel.transactions?.length || gameQuestionsLimit
  );

  const handleContinueButtonClick = () => {
    navigate(`/the-principles/level/${levelNumber}`);
  };

  return (
    <Layout isHeaderVisible={true} className="apc-intro-layout">
      <div className="apc-intro-wrapper">
        <div className="apc-intro-progress">
          <div className="apc-intro-progress-labels">
            <span>Progress</span>
            <span>Question 0 of {totalQuestions}</span>
          </div>
          <div className="apc-intro-progress-track">
            <div className="apc-intro-progress-fill" />
          </div>
        </div>

        <div className="apc-intro-card">
          <div className="apc-intro-card-body">
            {title && (
              <h2
                className="apc-intro-title"
                dangerouslySetInnerHTML={{ __html: title }}
              />
            )}
            {body && (
              <p
                className="apc-intro-description"
                dangerouslySetInnerHTML={{ __html: body }}
              />
            )}
          </div>
          <div className="apc-intro-card-footer">
            <button
              type="button"
              className="apc-intro-button"
              onClick={handleContinueButtonClick}
            >
              Let's Review
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
